import { useCallback, useEffect, useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { CaretLeft, CaretRight, ArrowSquareOut } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { formatMessageTime } from '../utils/dateUtils'

/**
 * "Ver" dos cartões de Métricas: lista as conversas por trás de um indicador
 * (reabertos pelo cliente, sem resposta humana) e mostra o chat ao lado, sem
 * sair da tela. "Abrir atendimento" leva para /all-chats/:id.
 */

export type IndicatorKey = 'reopened' | 'no_human_response'

export const INDICATOR_TITLES: Record<IndicatorKey, string> = {
  reopened: 'Reabertos pelo cliente',
  no_human_response: 'Sem resposta humana',
}

interface IndicatorChat {
  id: number
  protocol: string | null
  status: 'pending' | 'in_progress' | 'completed'
  createdAt: string
  closedBy: string | null
  ownerName: string | null
  ownerEmail: string | null
  ownerProfilePic: string | null
  agentName: string | null
  categoryName: string | null
  categoryColor: string | null
  lastMessage: string | null
  reopenCount: number
  isGuest: boolean
  unreadCountAgent: number
}

interface ChatMessage {
  id: number
  senderType: 'user' | 'agent' | 'system'
  senderName: string | null
  message: string | null
  messageType: string
  fileUrl: string | null
  fileName: string | null
  createdAt: string
}

const STATUS_LABEL: Record<IndicatorChat['status'], { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'yellow' },
  in_progress: { label: 'Andamento', color: 'blue' },
  completed: { label: 'Concluído', color: 'gray' },
}

function formatDateTime(value: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function IndicatorChatsModal({
  isOpen,
  onClose,
  indicator,
  startDate,
  endDate,
}: {
  isOpen: boolean
  onClose: () => void
  indicator: IndicatorKey | null
  startDate: string
  endDate: string
}) {
  const navigate = useNavigate()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const listBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const selectedBg = useColorModeValue('yellow.50', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const bubbleUser = useColorModeValue('gray.100', 'gray.700')
  const bubbleAgent = useColorModeValue('yellow.100', 'yellow.500')
  const bubbleAgentText = useColorModeValue('gray.800', 'gray.900')

  const [chats, setChats] = useState<IndicatorChat[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loadingList, setLoadingList] = useState(false)
  const [selected, setSelected] = useState<IndicatorChat | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const LIMIT = 20

  const fetchList = useCallback(async () => {
    if (!indicator) return
    setLoadingList(true)
    setErro(null)
    try {
      const res = await api.get('/admin/metrics/indicator-chats', {
        params: {
          indicator,
          startDate: `${startDate}T00:00:00`,
          endDate: `${endDate}T23:59:59`,
          page,
          limit: LIMIT,
          search: search || undefined,
        },
      })
      setChats(res.data.data || [])
      setTotal(res.data.meta?.total || 0)
      setLastPage(res.data.meta?.lastPage || 1)
    } catch {
      setErro('Não foi possível carregar as conversas.')
    } finally {
      setLoadingList(false)
    }
  }, [indicator, startDate, endDate, page, search])

  useEffect(() => {
    if (!isOpen) return
    fetchList()
  }, [isOpen, fetchList])

  // Reabre limpo a cada indicador/período
  useEffect(() => {
    if (!isOpen) return
    setPage(1)
    setSearch('')
    setSearchInput('')
    setSelected(null)
    setMessages([])
  }, [isOpen, indicator, startDate, endDate])

  useEffect(() => {
    if (!selected) return
    let cancelado = false
    setLoadingMessages(true)
    api
      .get(`/messages/${selected.id}`, { params: { limit: 200 } })
      .then((res) => {
        if (!cancelado) setMessages(res.data.data || [])
      })
      .catch(() => {
        if (!cancelado) setMessages([])
      })
      .finally(() => {
        if (!cancelado) setLoadingMessages(false)
      })
    return () => {
      cancelado = true
    }
  }, [selected])

  const aplicarBusca = () => {
    setPage(1)
    setSearch(searchInput.trim())
  }

  const abrirAtendimento = (id: number) => {
    onClose()
    navigate(`/all-chats/${id}`)
  }

  const title = indicator ? INDICATOR_TITLES[indicator] : ''

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={surfaceBg} maxH="90vh">
        <ModalHeader color={textPrimary}>
          <HStack justify="space-between" pr={8} flexWrap="wrap" gap={2}>
            <Text>{title}</Text>
            <Text fontSize="sm" color={textMuted} fontWeight="normal">
              {total} conversa{total === 1 ? '' : 's'} no período
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0}>
          <Flex h={{ base: 'auto', md: '70vh' }} direction={{ base: 'column', md: 'row' }}>
            {/* Lista */}
            <Flex
              direction="column"
              w={{ base: '100%', md: '380px' }}
              minW={{ md: '380px' }}
              borderRight={{ md: '1px solid' }}
              borderColor={borderColor}
              bg={listBg}
              maxH={{ base: '40vh', md: '100%' }}
            >
              <HStack p={3} spacing={2}>
                <Input
                  size="sm"
                  placeholder="Nome, e-mail ou protocolo"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') aplicarBusca()
                  }}
                />
                <Button size="sm" onClick={aplicarBusca} colorScheme="yellow" variant="outline">
                  Buscar
                </Button>
              </HStack>
              <Box flex={1} overflowY="auto">
                {loadingList ? (
                  <Flex justify="center" py={8}>
                    <Spinner color="yellow.500" />
                  </Flex>
                ) : erro ? (
                  <Text p={4} color="red.300" fontSize="sm">
                    {erro}
                  </Text>
                ) : chats.length === 0 ? (
                  <Text p={4} color={textMuted} fontSize="sm">
                    Nenhuma conversa neste recorte.
                  </Text>
                ) : (
                  chats.map((chat) => {
                    const st = STATUS_LABEL[chat.status] || STATUS_LABEL.pending
                    const ativo = selected?.id === chat.id
                    return (
                      <Box
                        key={chat.id}
                        px={3}
                        py={2}
                        cursor="pointer"
                        bg={ativo ? selectedBg : 'transparent'}
                        borderBottom="1px solid"
                        borderColor={borderColor}
                        onClick={() => setSelected(chat)}
                        _hover={{ bg: selectedBg }}
                      >
                        <HStack align="start" spacing={2}>
                          <Avatar size="sm" name={chat.ownerName || '?'} src={chat.ownerProfilePic || undefined} />
                          <VStack align="start" spacing={0} flex={1} minW={0}>
                            <HStack w="100%" justify="space-between">
                              <Text fontSize="sm" fontWeight="semibold" color={textPrimary} noOfLines={1}>
                                {chat.ownerName || 'Sem nome'}
                              </Text>
                              <Text fontSize="2xs" color={textMuted} flexShrink={0}>
                                {formatDateTime(chat.createdAt)}
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color={textMuted} noOfLines={1}>
                              {chat.ownerEmail}
                            </Text>
                            <HStack spacing={1} mt={1} flexWrap="wrap">
                              <Badge fontSize="9px" colorScheme={st.color}>
                                {st.label}
                              </Badge>
                              {chat.categoryName && (
                                <Badge fontSize="9px" bg={chat.categoryColor || 'blue.500'} color="white">
                                  {chat.categoryName}
                                </Badge>
                              )}
                              {chat.isGuest && (
                                <Badge fontSize="9px" colorScheme="purple">
                                  Visitante
                                </Badge>
                              )}
                              {chat.reopenCount > 0 && (
                                <Badge fontSize="9px" colorScheme="orange">
                                  Reaberto {chat.reopenCount > 1 ? `${chat.reopenCount}x` : ''}
                                </Badge>
                              )}
                            </HStack>
                            {chat.lastMessage && (
                              <Text fontSize="xs" color={textMuted} noOfLines={1} mt={1}>
                                {chat.lastMessage}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    )
                  })
                )}
              </Box>
              <HStack p={2} justify="space-between" borderTop="1px solid" borderColor={borderColor}>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<CaretLeft size={14} />}
                  isDisabled={page <= 1 || loadingList}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Text fontSize="xs" color={textMuted}>
                  {page} / {lastPage}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  rightIcon={<CaretRight size={14} />}
                  isDisabled={page >= lastPage || loadingList}
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                >
                  Próxima
                </Button>
              </HStack>
            </Flex>

            {/* Chat */}
            <Flex direction="column" flex={1} minW={0}>
              {!selected ? (
                <Flex flex={1} align="center" justify="center" p={6}>
                  <Text color={textMuted} fontSize="sm">
                    Selecione uma conversa para ler o chat.
                  </Text>
                </Flex>
              ) : (
                <>
                  <HStack p={3} borderBottom="1px solid" borderColor={borderColor} justify="space-between" flexWrap="wrap" gap={2}>
                    <VStack align="start" spacing={0} minW={0}>
                      <Text fontWeight="semibold" color={textPrimary} noOfLines={1}>
                        {selected.ownerName || 'Sem nome'}
                        {selected.protocol ? ` · ${selected.protocol}` : ''}
                      </Text>
                      <Text fontSize="xs" color={textMuted} noOfLines={1}>
                        {selected.categoryName || 'Sem categoria'}
                        {selected.agentName ? ` · atendente: ${selected.agentName}` : ' · sem atendente'}
                        {selected.closedBy ? ` · encerrado por ${selected.closedBy === 'system' ? 'inatividade' : selected.closedBy === 'agent' ? 'atendente' : 'cliente'}` : ''}
                      </Text>
                    </VStack>
                    <Button
                      size="sm"
                      colorScheme="yellow"
                      rightIcon={<ArrowSquareOut size={16} />}
                      onClick={() => abrirAtendimento(selected.id)}
                    >
                      Abrir atendimento
                    </Button>
                  </HStack>
                  <Box flex={1} overflowY="auto" p={4}>
                    {loadingMessages ? (
                      <Flex justify="center" py={8}>
                        <Spinner color="yellow.500" />
                      </Flex>
                    ) : messages.length === 0 ? (
                      <Text color={textMuted} fontSize="sm">
                        Sem mensagens.
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={2}>
                        {messages.map((m) => {
                          if (m.senderType === 'system') {
                            return (
                              <Text key={m.id} fontSize="xs" color={textMuted} textAlign="center" px={4}>
                                {m.message}
                              </Text>
                            )
                          }
                          const doAgente = m.senderType === 'agent'
                          return (
                            <Flex key={m.id} justify={doAgente ? 'flex-end' : 'flex-start'}>
                              <Box
                                maxW="75%"
                                bg={doAgente ? bubbleAgent : bubbleUser}
                                color={doAgente ? bubbleAgentText : textPrimary}
                                px={3}
                                py={2}
                                borderRadius="lg"
                              >
                                <Text fontSize="xs" fontWeight="semibold" mb={1} color={doAgente ? bubbleAgentText : textMuted}>
                                  {m.senderName || (doAgente ? 'Atendente' : 'Cliente')}
                                </Text>
                                {m.messageType && m.messageType !== 'text' ? (
                                  <Text fontSize="sm" fontStyle="italic" color={doAgente ? bubbleAgentText : textPrimary}>
                                    [{m.messageType}] {m.fileName || ''}
                                  </Text>
                                ) : (
                                  <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word" color={doAgente ? bubbleAgentText : textPrimary}>
                                    {m.message}
                                  </Text>
                                )}
                                <Text fontSize="10px" opacity={0.7} textAlign="right" mt={1} color={doAgente ? bubbleAgentText : textPrimary}>
                                  {formatMessageTime(m.createdAt)}
                                </Text>
                              </Box>
                            </Flex>
                          )
                        })}
                      </VStack>
                    )}
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
